import { BaseModel } from './base-model';
import { Customer } from './customer';
import { User } from './user';
import { ParecerIndicativo } from './indicator';
import { ProductConfiguration } from './product-configuration';
import { calculatePercent, calculateDifferenceInDays } from '../shared/helpers/utils';
import { CriterionGroup, Criterion, CriterionValue } from './criterion';
import { UploadService } from '../shared/components/upload/upload.service';
import { MxAlertService } from 'mx-commons';
import { EventEmitter } from '@angular/core';
import { CicloViewModel } from '../layout/financing/components/ciclo-documentos/ciclo-documentos.component';
import { CriterionViewModel } from '../layout/financing/components/avaliacao-cinco/avaliacao-cinco.component';

export class AnalyzeCredit extends BaseModel {

	public id?: string;
	public date: number;
	public companyId: string;
	public customer: Customer;
	public product?: string;
	public revolvingCredit?: string;
	public productYear?: number;
	public effectiveDate?: number;
	public salesOrderNumber?: number;
	public revolvingCreditValue?: number;
	public saleValue?: number;
	public costValue?: number;
	public inputValue?: number;
	public movementValue?: number;
	public purchaseDate?: number;
	public lastPaymentDate?: number;
	public status: AnalysisStatusEnum;
	public typeAnalyse: TypeAnalyseEnum;
	public typeDelivery: TypeDeliveryEnum;
	public steps: Array<AnalyzeStep>;
	public attachmentOrder: Array<AttachmentInfo>;
	public attachmentSalesReport: Array<AttachmentInfo>;
	public attachmentNFe: Array<AttachmentInfo>;
	public attachmentContract: Array<AttachmentInfo>;
	public resultIndex: ResultIndexAnalyse;
	public infosInputValue: Array<InfoInputValue>;
	public parcel: Array<Parcel>;
	public analyzeVersion: number;
	public bankValue?: number;
	public secondhandValue?: number;

	constructor() {
		super();
		this.id = null;
		this.date = Date.now();
		this.companyId = null;
		this.customer = null;
		this.product = null;
		this.revolvingCredit = null;
		this.productYear = null;
		this.effectiveDate = null;
		this.salesOrderNumber = null;
		this.revolvingCreditValue = null;
		this.saleValue = null;
		this.costValue = null;
		this.inputValue = null;
		this.movementValue = null;
		this.purchaseDate = null;
		this.lastPaymentDate = null;
		this.status = AnalysisStatusEnum.OPEN;
		this.typeAnalyse = null;
		this.typeDelivery = null;
		this.createdAt = null;
		this.updatedAt = null;
		this.steps = new Array<AnalyzeStep>();
		this.attachmentOrder = new Array<AttachmentInfo>();
		this.attachmentSalesReport = new Array<AttachmentInfo>();
		this.attachmentNFe = new Array<AttachmentInfo>();
		this.attachmentContract = new Array<AttachmentInfo>();
		this.resultIndex = null;
		this.infosInputValue = null;
		this.parcel = new Array<Parcel>();
		this.secondhandValue = null;
		this.bankValue = null;
	}

}

export enum AnalysisStatusEnum {
	OPEN,
	ANALYZE,
	PASSED,
	FAILED,
	CANCELED
}

export enum TypeAnalyseEnum {
	WITH_CASHFLOW,
	NO_CASHFLOW,
	CREDITO_ROTATIVO,
	AFTER_SALES
}

export enum TypeDeliveryEnum {
	NORMAL,
	ANTICIPATED
}

export class AnalyzeStep {

	public step: StepAnalyseEnum;
	public status: AnalysisStatusEnum;
	public date?: number;
	public user?: User;

	constructor() {
		this.step = null;
		this.status = AnalysisStatusEnum.OPEN;
		this.date = null;
		this.user = null;
	}

}

export enum StepAnalyseEnum {
	GERENTE_ADMINISTRATIVO,
	GERENTE_LOJA,
	CORPORATIVO,
	DIRETOR_COMERCIAL,
	DIRETOR_PRESIDENTE,
	VISUALIZAR
}


export class ResultIndexAnalyse {

	public income: number;
	public debt: number;
	public committedValue: number;
	public committed: number;
	public configCommitted: number;
	public acceptance: number;
	public configAcceptance: Array<ParecerIndicativo>;
	public resultAcceptance: ParecerIndicativo;

	constructor() {
		this.income = null;
		this.debt = null;
		this.committedValue = null;
		this.committed = null;
		this.configCommitted = null;
		this.acceptance = null;
		this.configAcceptance = new Array<ParecerIndicativo>();
		this.resultAcceptance = null;
	}

}

export class MachineCapitalAnalyse extends BaseModel {

	public id?: string;
	public description: string;
	public value: number;
	public free: boolean;

	constructor() {
		super();
		this.id = null;
		this.description = null;
		this.value = null;
		this.free = null;
	}

}

export class IncomePlantingAnalyse extends BaseModel {

	public id?: string;
	public description: string;
	public type: PlantingAreaEnum;
	public amount: number;
	public value: number;
	public product: ProductConfiguration;

	constructor() {
		super();
		this.id = null;
		this.description = null;
		this.type = null;
		this.amount = null;
		this.value = null;
		this.product = null;
	}

}

export enum PlantingAreaEnum {

	OWN,
	LEASED,
	OTHER

}

export class DebtAnalyse extends BaseModel {

	public id?: string;
	public description: string;
	public type: DebtTypeEnum;
	public value: number;

	constructor() {
		super();
		this.id = null;
		this.description = null;
		this.type = null;
		this.value = null;
	}

}

export enum DebtTypeEnum {

	CONTRACT,
	OTHER,
	PERSONAL_COST,
	PARCEL_YEARLY

}

export class AcceptanceIndexAnalyse extends BaseModel {

	public id?: String;
	public inputOnSale: InputOnSale;
	public inputOnCost: InputOnCost;
	public committedLimit: CommittedLimit;
	public contributionMargin: ContributionMargin;
	public saleTerm: SaleTerm;
	public timeInStock: TimeInStock;

	constructor() {
		super();
		this.id = null;
		this.inputOnSale = new InputOnSale();
		this.inputOnCost = new InputOnCost();
		this.committedLimit = new CommittedLimit();
		this.contributionMargin = new ContributionMargin();
		this.saleTerm = new SaleTerm();
		this.timeInStock = new TimeInStock();
	}

}

export class InputOnSale {
	constructor(public received: number = 0, public sale: number = 0, public result: number = 0, public config: number = 0) {
	}

	static new(object: InputOnSale): InputOnSale {
		return new InputOnSale(object.received, object.sale, object.result, object.config);
	}

	public calculateResult() {
		this.result = calculatePercent(this.received, this.sale, 2);
	}
}

export class InputOnCost {
	constructor(public received: number = 0, public cost: number = 0, public result: number = 0, public config: number = 0) {
	}

	static new(object: InputOnCost): InputOnCost {
		return new InputOnCost(object.received, object.cost, object.result, object.config);
	}

	public calculateResult() {
		this.result = calculatePercent(this.received, this.cost, 2);
	}
}

export class CommittedLimit {
	constructor(public receivable: number = 0, public planned: number = 0, public result: number = 0, public config: number = 0) {
	}

	static new(object: CommittedLimit): CommittedLimit {
		return new CommittedLimit(object.receivable, object.planned, object.result, object.config);
	}

	public calculateResult() {
		this.result = calculatePercent(this.receivable, this.planned, 2);
	}
}

export class ContributionMargin {
	constructor(public cost: number = 0, public sale: number = 0, public result: number = 0, public config: number = 0) {
	}

	static new(object: ContributionMargin): ContributionMargin {
		return new ContributionMargin(object.cost, object.sale, object.result, object.config);
	}

	public calculateResult() {
		this.result = 100 - calculatePercent(this.cost, this.sale, 2);
	}
}

export class SaleTerm {
	constructor(public sale: number = 0, public lastPayment: number = 0, public result: number = 0, public config: number = 0) {
	}

	static new(object: SaleTerm): SaleTerm {
		return new SaleTerm(object.sale, object.lastPayment, object.result, object.config);
	}

	public calculateResult() {
		this.result = calculateDifferenceInDays(this.sale, this.lastPayment);
	}
}

export class TimeInStock {
	constructor(public sale: number = 0, public input: number = 0, public result: number = 0, public config: number = 0) {
	}

	static new(object: TimeInStock): TimeInStock {
		return new TimeInStock(object.sale, object.input, object.result, object.config);
	}

	public calculateResult() {
		this.result = calculateDifferenceInDays(this.input, this.sale);
	}
}

export class CriterionEvaluationAnalyse extends BaseModel {

	public id: string;
	public group: CriterionGroup;
	public criterion: Criterion;
	public value: CriterionValue;
	public consideration: string;
	public attachment: Array<AttachmentInfo>;

	constructor() {
		super();
		this.id = null;
		this.group = null;
		this.criterion = null;
		this.value = null;
		this.consideration = null;
		this.attachment = null;
	}

}

export enum InfoInputValueEnum {

	OTHER,
	AUTOMOBILE,
	MACHINE,
	PROPERTY,
	FINANCIAL,
	CONSORTIUMS

}

export class InfoInputValue {

	public type: InfoInputValueEnum;
	public description: string;

	constructor() {
		this.type = null;
		this.description = null;
	}

	static new(type: InfoInputValueEnum, description: string): InfoInputValue {
		const info = new InfoInputValue();
		info.type = type;
		info.description = description;
		return info;
	}

}


export enum RatingEnum {
	C,
	CP,
	CC,
	CCC,
	B,
	BP,
	BBB,
	A,
	AP,
	AA,
	AAA,
}

export enum FiscalYearEnum {
	CURRENT,
	LAST,
	CUSTOM
}

export interface AttachmentInfo {
	body: AttachmentInfo;
	attachmentId: string;
	description: string;
	file?: File;
}

export interface ModalUploadData {
	analyze: AnalyzeCredit;
	ciclo: CicloViewModel;
	criterion: CriterionViewModel;
	uploadService: UploadService;
	alertService: MxAlertService;
	mxUploading: EventEmitter<boolean>;
	lgpdArray?:Array<AttachmentInfo>;
}

export class Parcel extends BaseModel {
	public id: string;
	public number: number;
	public value: number;
	public paymentDate: number;
	public analyseId: string;

	constructor() {
		super()
		this.paymentDate = null;
		this.number = null;
		this.value = null;
	}
}
